/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Removes specified items from the cart.
 *
 * @function removeItemsFromCart
 * @param {Array<number>} variantIds - An array of variant IDs to remove from the cart.
 * @returns {Promise<void>}
 */
async function removeItemsFromCart(variantIds) {
  const updates = variantIds.reduce((acc, id) => {
    acc[id] = 0;
    return acc;
  }, {});

  return fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({ updates }),
  })
    .then((response) => response.json())
    .then(() => {
      setTimeout(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
          })
        );
      }, 1000);
    })
    .catch((error) => {
      console.error("Failed to remove products from cart:", error);
    });
}

document.addEventListener("cart:updated", function (event) {
  var cart = event.detail.cart;

  const filteredItems = cart.items
    .filter((item) => {
      return item.properties?._BundleParentId;
    })
    .map((item) => {
      return item.id;
    });

  if (filteredItems.length > 0) {
    removeItemsFromCart(filteredItems);
  }
});

if (!customElements.get("swiper-element")) {
  customElements.define(
    "swiper-element",
    class SwiperElement extends HTMLElement {
      constructor() {
        super();
        this.slider = null;
        this.config = JSON.parse(this.getAttribute("swiper-config"));
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.slider.destroy(true, true);
      }

      init() {
        this.slider = new Swiper(this, this.config);
      }

      get swiperInstance() {
        return this.slider;
      }
    }
  );
}

if (!customElements.get("content-modal")) {
  customElements.define(
    "content-modal",
    class ContentModal extends HTMLElement {
      constructor() {
        super();
        this.trigger = this.querySelector("div[data-trigger]");
        this.content = this.querySelector("div[data-content]");
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {}

      init() {
        this.trigger.addEventListener("click", () => {
          if (this.content.hasAttribute("open")) {
            this.content.removeAttribute("open");
            this.trigger.removeAttribute("active");
          } else {
            this.content.setAttribute("open", "");
            this.trigger.setAttribute("active", "");
          }
        });
      }
    }
  );
}

// Shipping Info

function changeShippingInfo(variant) {
  const shippingInfoBox = document.querySelector(".shipping-info__normal")
    .children[0];
  const shippingSpecialInfo = document.querySelector(
    ".shipping-info__special-text"
  );
  const { shippingInfo } = window;

  if (variant.available) {
    shippingInfoBox.textContent = shippingInfo.inStock;
    shippingInfoBox.classList.remove("shipping-info--out-of-stock");
    shippingInfoBox.classList.add("shipping-info--in-stock");

    if (shippingSpecialInfo) {
      shippingSpecialInfo.textContent = shippingInfo.specialInfo;
    }
  } else {
    shippingInfoBox.textContent = shippingInfo.outOfStock;
    shippingInfoBox.classList.remove("shipping-info--in-stock");
    shippingInfoBox.classList.add("shipping-info--out-of-stock");

    if (shippingSpecialInfo) {
      shippingSpecialInfo.textContent = "";
    }
  }
}

document.addEventListener("variant:changed", function (event) {
  let variant = event.detail.variant; // Gives you access to the whole variant details
  changeShippingInfo(variant);
});

var swiper = new Swiper(".wc-product-thumb.mySwiper", {
  slidesPerView: "4",
  freeMode: true,
  watchSlidesProgress: true,
  mousewheel: true,
  pagination: {
    el: ".swiper-pagination",
    type: "progressbar",
  },
  breakpoints: {
    640: {
      slidesPerView: 4,
    },
    768: {
      direction: "vertical",
      slidesPerView: "auto",
      autoHeight: true,
    },
    1024: {
      direction: "vertical",
      slidesPerView: "auto",
      autoHeight: true,
    },
  },
});
var swiper2 = new Swiper(".wc-product-media.mySwiper2", {
  spaceBetween: 10,
  navigation: {
    nextEl: ".swiper-button-next",
    prevEl: ".swiper-button-prev",
  },
  thumbs: {
    swiper: swiper,
  },
});

document.addEventListener("DOMContentLoaded", function () {
  var reviewmyInterval = setInterval(review_widgetmyTimer, 1000);

  function review_widgetmyTimer() {
    var reviewWidgets = document.querySelectorAll(".jdgm-widget");

    if (reviewWidgets.length > 0) {
      reviewWidgets.forEach(function (widget) {
        var thumbnailLinks = widget.querySelectorAll(
          ".jdgm-row-media .jdgm-gallery-wrapper .jdgm-gallery .jdgm-gallery__thumbnail-link"
        );

        if (thumbnailLinks.length > 0) {
          thumbnailLinks.forEach(function (link) {
            var thumbnail = link.querySelector(".jdgm-gallery__thumbnail");
            if (thumbnail) {
              var dataSrc = thumbnail.getAttribute("data-src");
              if (dataSrc) {
                var urlObj = new URL(dataSrc);
                urlObj.search = ""; // Remove query parameters
                var customUrl = urlObj.toString();
                thumbnail.setAttribute("src", customUrl);
              }
            }
          });
        }
      });
      reviewmyStopFunction();
    }
  }

  function reviewmyStopFunction() {
    //clearInterval(reviewmyInterval);
  }
});

document.addEventListener("DOMContentLoaded", function () {
  // Select all <h4> elements inside '.title-with-submenu'
  const titles = document.querySelectorAll(".title-with-submenu h4");

  titles.forEach(function (title) {
    title.addEventListener("click", function () {
      // Toggle active class on the parent '.title-with-submenu'
      const parent = this.parentElement;
      parent.classList.toggle("active");

      // Find the next sibling menu
      const menu = parent.nextElementSibling;

      if (menu && menu.classList.contains("menu")) {
        if (menu.style.maxHeight) {
          // Collapse the menu
          menu.style.maxHeight = null;
        } else {
          // Expand the menu
          menu.style.maxHeight = menu.scrollHeight + "px";
        }
      }

      // Optional accordion behavior: Close other menus
      const otherTitles = document.querySelectorAll(".title-with-submenu h4");
      otherTitles.forEach(function (otherTitle) {
        const otherParent = otherTitle.parentElement;
        const otherMenu = otherParent.nextElementSibling;

        if (otherTitle !== title) {
          otherParent.classList.remove("active");
          if (otherMenu && otherMenu.classList.contains("menu")) {
            otherMenu.style.maxHeight = null;
          }
        }
      });
    });
  });

  let SwiperTop = new Swiper(".logo-swiper-slider.swiper--top", {
    spaceBetween: 15,
    slidesPerView: 5,
    //centeredSlides: true,
    speed: 2000,
    autoplay: {
      delay: 0,
      disableOnInteraction: false,
    },
    breakpoints: {
      480: {
        spaceBetween: 25,
        slidesPerView: 6,
      },
      640: {
        spaceBetween: 25,
        slidesPerView: 7,
      },
      768: {
        spaceBetween: 30,
        slidesPerView: 8,
      },
      1024: {
        spaceBetween: 30,
        slidesPerView: 10,
      },
      1200: {
        spaceBetween: 45,
        slidesPerView: 14,
      },
      1500: {
        spaceBetween: 45,
        slidesPerView: 15,
      },
    },
    loop: true,
    //allowTouchMove: false,
  });
});
//new arrodian for size chart
/*
document.addEventListener("DOMContentLoaded", function () {
  const data = window.productExtraAccordion;

  if (!data || !data.image) return;
  const wrapper = document.querySelector(".product-content.anchor");
  if (!wrapper) return;
  const tabs = wrapper.querySelector(".product-tabs__content");
  if (!tabs) return;
  const accordions = tabs.querySelectorAll(".product-tabs__tab-item-wrapper");
  if (!accordions.length) return;

  const uid = "extra-image-" + Date.now();

  const accordion = document.createElement("div");
  accordion.className = "product-tabs__tab-item-wrapper";
  accordion.innerHTML = `
    <button is="toggle-button" class="collapsible-toggle heading heading--small" aria-expanded="false" aria-controls="${uid}"onclick="openSize()">
      Größentabelle <span class="faq-icon"></span>
    </button>
    <collapsible-content id="${uid}" class="collapsible tabsize_chart">
      <div class="product-tabs__tab-item-content rte">
        <div class="size-chart-trigger-wrapper">
          <img src="${data.image}" alt="${data.alt || 'Size chart'}" class="size-chart-preview" loading="lazy">
          <div class="size-chart-overlay">
            <span class="zoom-icon">🔍</span>
            <span>Vergrößern</span>
          </div>
        </div>
        <p class="size-chart-hint">ⓘ Tippe um zu Zoomen</p>
      </div>
    </collapsible-content>
  `;
  tabs.insertBefore(accordion, accordions[accordions.length - 1]);

  const modalHTML = `
    <div id="size-chart-modal" class="sc-modal">
      <button class="sc-modal-close">&times;</button>
      <div class="sc-modal-content">
        <img src="${data.image}" class="sc-modal-image" alt="Full Size Chart">
      </div>
    </div>
  `;
  document.body.insertAdjacentHTML('beforeend', modalHTML);

  const trigger = accordion.querySelector(".size-chart-trigger-wrapper");
  const modal = document.getElementById("size-chart-modal");
  const closeBtn = modal.querySelector(".sc-modal-close");
  const img = modal.querySelector(".sc-modal-image");
  
  let scale = 1;
  let pointX = 0;
  let pointY = 0;
  let startX = 0;
  let startY = 0;
  let isDragging = false;
  let startDist = 0; 

  trigger.addEventListener("click", () => {
    modal.classList.add("active");
    document.body.style.overflow = "hidden"; 
    resetZoom();
  });

  const closeModal = () => {
    modal.classList.remove("active");
    document.body.style.overflow = "";
  };

  closeBtn.addEventListener("click", closeModal);
  
  modal.addEventListener("click", (e) => {
    if (e.target === modal || e.target.classList.contains("sc-modal-content")) {
      closeModal();
    }
  });

  function updateTransform() {
    img.style.transform = `translate(${pointX}px, ${pointY}px) scale(${scale})`;
  }
  
  function resetZoom() {
    scale = 1; pointX = 0; pointY = 0;
    updateTransform();
  }

  img.addEventListener("touchstart", function (e) {
    if (e.touches.length === 2) {
      
      e.preventDefault(); 
      startDist = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY
      );
    } else if (e.touches.length === 1) {
    
      isDragging = true;
      startX = e.touches[0].clientX - pointX;
      startY = e.touches[0].clientY - pointY;
    }
  });

  img.addEventListener("touchmove", function (e) {
    e.preventDefault(); 
    
    if (e.touches.length === 2) {
      
      const dist = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY
      );
      const zoomSpeed = 0.005; 
      const delta = dist - startDist;
      
      scale += delta * zoomSpeed;
      scale = Math.min(Math.max(1, scale), 5); 
      
      startDist = dist; 
      updateTransform();
      
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      pointX = e.touches[0].clientX - startX;
      pointY = e.touches[0].clientY - startY;
      updateTransform();
    }
  });

  img.addEventListener("touchend", function () {
    isDragging = false;
    if (scale < 1) { scale = 1; pointX = 0; pointY = 0; updateTransform(); }
  });

  img.addEventListener("mousedown", (e) => {
    if (scale > 1) {
      isDragging = true;
      startX = e.clientX - pointX;
      startY = e.clientY - pointY;
      img.style.cursor = "grabbing";
      e.preventDefault();
    }
  });

  window.addEventListener("mousemove", (e) => {
    if (isDragging) {
      pointX = e.clientX - startX;
      pointY = e.clientY - startY;
      updateTransform();
    }
  });

  window.addEventListener("mouseup", () => {
    isDragging = false;
    img.style.cursor = scale > 1 ? "grab" : "zoom-in";
  });

  img.addEventListener("dblclick", () => {
    if (scale === 1) {
      scale = 2.5;
      img.style.cursor = "grab";
    } else {
      resetZoom();
      img.style.cursor = "zoom-in";
    }
    updateTransform();
  });
});
*/
/**
 * ------------------------------------------------------------------------------------------------------------
 * PRODUCT PAGE PRICE AGGREGATION (add-to-cart button price = bundle app price + extras)
 * ------------------------------------------------------------------------------------------------------------
 *
 * The "Kaching Bundles" app writes the price of the currently selected bundle tier (1er/3er/...) directly into
 * our own #AddToCart button (it looks for a price-looking text node inside the button and overwrites it). We
 * can't query that price from Kaching directly (no public API for it), so instead we observe our own button and
 * treat whatever Kaching writes there as the "base price".
 *
 * Anything that should be added on top of the bundle price (the "KAUFE DAZU" add-on checkboxes today, an upsell
 * widget later) registers itself as a named price source in cents:
 *
 * window.naildPricing.setSource('my-source-name', amountInCents);
 * window.naildPricing.removeSource('my-source-name');
 *
 * Whenever a source changes, the button price is automatically recomputed as (base price from Kaching) + (sum
 * of all registered sources) and re-rendered. No other code needs to change when a new source is added.
 */
(function () {
  var sources = {};

  function setSource(name, cents) {
    var value = parseInt(cents, 10) || 0;
    if (sources[name] === value) return;
    sources[name] = value;
    document.dispatchEvent(new CustomEvent("naild:price-sources-changed"));
  }

  function removeSource(name) {
    if (!(name in sources)) return;
    delete sources[name];
    document.dispatchEvent(new CustomEvent("naild:price-sources-changed"));
  }

  function extrasTotal() {
    return Object.keys(sources).reduce(function (sum, key) {
      return sum + sources[key];
    }, 0);
  }

  window.naildPricing = {
    setSource: setSource,
    removeSource: removeSource,
    extrasTotal: extrasTotal
  };

  function parseMoneyToCents(text) {
    if (!text) return null;

    var cleaned = text.replace(/[^0-9.,]/g, "").trim();
    if (!cleaned) return null;

    var decimalIndex = Math.max(cleaned.lastIndexOf(","), cleaned.lastIndexOf("."));
    var integerPart, decimalPart;

    if (decimalIndex === -1) {
      integerPart = cleaned;
      decimalPart = "00";
    } else {
      integerPart = cleaned.slice(0, decimalIndex).replace(/[.,]/g, "");
      decimalPart = cleaned.slice(decimalIndex + 1).replace(/[^0-9]/g, "");

      if (decimalPart.length !== 2) {
        integerPart = cleaned.replace(/[.,]/g, "");
        decimalPart = "00";
      }
    }

    var cents = parseInt(integerPart || "0", 10) * 100 + parseInt(decimalPart || "0", 10);
    return isNaN(cents) ? null : cents;
  }

  function updateStickyBarDisplays(totalCents) {
    if (typeof formatMoney !== "function") return;

    var formatted = formatMoney(totalCents);
    document.querySelectorAll("[data-sticky-price-mirror]").forEach(function (el) {
      if (el.textContent !== formatted) el.textContent = formatted;
    });

    var badge = document.querySelector("[data-free-shipping-badge]");
    if (!badge) return;

    var threshold = parseInt(badge.dataset.freeShippingThreshold, 10);
    if (isNaN(threshold)) return;

    var textElement = badge.querySelector("[data-free-shipping-text]");
    var unlocked = totalCents >= threshold;

    badge.classList.toggle("product-sticky-form__mobile-shipping--pending", !unlocked);

    if (textElement) {
      textElement.textContent = unlocked
        ? "Gratis Versand"
        : "noch " + formatMoney(threshold - totalCents) + " bis Gratisversand";
    }
  }

  function observeAddToCartPrice(priceElement) {
    if (!priceElement || priceElement.dataset.naildPriceObserved) return;
    priceElement.dataset.naildPriceObserved = "true";

    var basePriceCents = parseMoneyToCents(priceElement.textContent);
    var lastRenderedText = null;

    function render() {
      if (basePriceCents == null || typeof formatMoney !== "function") return;

      var totalCents = basePriceCents + extrasTotal();
      var formatted = formatMoney(totalCents);

      if (formatted !== priceElement.textContent) {
        lastRenderedText = formatted;
        priceElement.textContent = formatted;
      }

      updateStickyBarDisplays(totalCents);
    }

    var observer = new MutationObserver(function () {
      var currentText = priceElement.textContent;
      if (currentText === lastRenderedText) return;

      var parsed = parseMoneyToCents(currentText);
      if (parsed == null) return;

      basePriceCents = parsed;
      render();
    });

    observer.observe(priceElement, { childList: true, characterData: true, subtree: true });
    document.addEventListener("naild:price-sources-changed", render);

    render();
  }

  document
    .querySelectorAll("#AddToCart .product-form__add-button-price, #StickyAddToCart .product-form__add-button-price")
    .forEach(observeAddToCartPrice);

  document.addEventListener("bundle-set:switched", function () {
    document
      .querySelectorAll("#AddToCart .product-form__add-button-price, #StickyAddToCart .product-form__add-button-price")
      .forEach(observeAddToCartPrice);
  });

  function updateAddonTotal() {
    var checked = document.querySelectorAll("input[data-checkbox-upsell]:checked");
    var sum = 0;

    checked.forEach(function (input) {
      sum += parseInt(input.dataset.priceCents, 10) || 0;
    });

    setSource("addon", sum);
  }

  document.addEventListener("change", function (event) {
    if (event.target.matches && event.target.matches("input[data-checkbox-upsell]")) {
      updateAddonTotal();
    }
  });

  updateAddonTotal();
})();
/**
 * Sticky ATC: visible whenever the main ATC button is NOT in the viewport
 */
(function () {
  function initStickyForm(stickyForm) {
    if (stickyForm.dataset.naildStickyInit) return;
    stickyForm.dataset.naildStickyInit = "true";

    var formId = stickyForm.getAttribute("form-id");
    var mainForm = formId && document.getElementById(formId);
    var target =
      (mainForm && mainForm.querySelector("product-payment-container")) ||
      document.getElementById("AddToCart");

    if (!target) return;

    var shouldHide = false;

    function apply() {
      if (shouldHide) {
        if (!stickyForm.hasAttribute("hidden")) stickyForm.setAttribute("hidden", "");
      } else {
        if (stickyForm.hasAttribute("hidden")) stickyForm.removeAttribute("hidden");
      }
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          shouldHide = entry.isIntersecting;
          apply();
        });
      },
      { rootMargin: "0px 0px -60px 0px", threshold: 0 }
    );
    io.observe(target);

    var mo = new MutationObserver(apply);
    mo.observe(stickyForm, { attributes: true, attributeFilter: ["hidden"] });
  }

  function run() {
    document.querySelectorAll("product-sticky-form").forEach(initStickyForm);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  document.addEventListener("shopify:section:load", run);
  document.addEventListener("bundle-set:switched", run);
})();

if (!customElements.get("bundle-set-switcher")) {
  customElements.define(
    "bundle-set-switcher",
    class BundleSetSwitcher extends HTMLElement {
      constructor() {
        super();
        this.sectionCache = new Map();
        this.abortController = null;
        this.selectors = [];
        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        this.selectors = Array.from(this.querySelectorAll("[data-handle]"));
        this.selectors.forEach((selector) => selector.addEventListener("click", this.onChange));
        this.rememberCurrentPage();
        this.prefetchSets();
      }

      disconnectedCallback() {
        this.selectors.forEach((selector) => selector.removeEventListener("click", this.onChange));
        if (this.abortController) this.abortController.abort();
      }

      get sectionParam() {
        return [this.dataset.sectionId, this.dataset.drawerSectionId].filter(Boolean).join(",");
      }

      sectionUrl(handle) {
        return `${window.Shopify.routes.root}products/${handle}?sections=${this.sectionParam}`;
      }

      parseHtml(html) {
        const template = document.createElement("template");
        template.innerHTML = html.trim();
        return template.content.firstElementChild;
      }

      rememberCurrentPage() {
        const current = this.selectors.find((selector) => selector.hasAttribute("aria-current"));
        if (!current || this.sectionCache.has(current.dataset.handle)) return;

        const payload = {};
        const mainEl = document.querySelector(".shopify-section--main-product");
        const drawerEl = document.querySelector(".shopify-section--product-drawer");
        if (this.dataset.sectionId && mainEl) payload[this.dataset.sectionId] = mainEl.outerHTML;
        if (this.dataset.drawerSectionId && drawerEl) {
          payload[this.dataset.drawerSectionId] = drawerEl.outerHTML;
        }
        this.sectionCache.set(current.dataset.handle, payload);
      }

      prefetchSets() {
        this.selectors.forEach((selector) => {
          if (selector.hasAttribute("aria-current")) return;
          const handle = selector.dataset.handle;
          if (!handle || this.sectionCache.has(handle)) return;

          fetch(this.sectionUrl(handle), { priority: "low" })
            .then((response) => response.json())
            .then((data) => this.sectionCache.set(handle, data))
            .catch(() => {});
        });
      }

      onChange(event) {
        const selector = event.currentTarget;
        const handle = selector.dataset.handle;
        if (!handle || selector.hasAttribute("aria-current")) return;

        const url = this.sectionUrl(handle);
        const cached = this.sectionCache.get(handle);
        if (cached) {
          this.renderSet(cached, selector);
          return;
        }

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        document.querySelector(".bundle-picker")?.classList.add("is-switching");

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.json())
          .then((data) => {
            this.sectionCache.set(handle, data);
            this.renderSet(data, selector);
          })
          .catch((error) => {
            if (error.name === "AbortError") return;
            console.error("Failed to switch bundle set:", error);
          })
          .finally(() => {
            document.querySelector(".bundle-picker")?.classList.remove("is-switching");
          });
      }

      renderSet(data, selector) {
        const selections = this.captureSelections();
        const incomingMain = data[this.dataset.sectionId]
          ? this.parseHtml(data[this.dataset.sectionId])
          : null;

        if (incomingMain) {
          this.replaceNode(".bundle-picker__main", incomingMain);
          this.replaceNode(".optional-products", incomingMain, ".bundle-picker");
          this.replaceNode(".bundle-picker__additional-products", incomingMain, ".bundle-picker");
          this.replaceNode("product-meta", incomingMain);
          this.updateForm(incomingMain);
        }

        if (this.dataset.drawerSectionId && data[this.dataset.drawerSectionId]) {
          const nextDrawer = this.parseHtml(data[this.dataset.drawerSectionId]);
          const currentDrawer = document.querySelector(".shopify-section--product-drawer");
          if (nextDrawer && currentDrawer) currentDrawer.replaceWith(nextDrawer);
        }

        this.restoreSelections(selections);
        this.updateActive(selector.dataset.handle);

        const title = document.querySelector(".product-meta__title");
        if (title) document.title = title.textContent.trim();

        document.dispatchEvent(
          new CustomEvent("bundle-set:switched", {
            bubbles: true,
            detail: { handle: selector.dataset.handle, url: selector.dataset.url },
          })
        );
        document.dispatchEvent(new CustomEvent("shopify:section:load", { bubbles: true }));

        if (window.jdgm && typeof window.jdgm.customizeBadges === "function") {
          window.jdgm.customizeBadges();
        }
      }

      replaceNode(selector, incoming, appendParentSelector) {
        const next = incoming.querySelector(selector);
        const current = document.querySelector(selector);

        if (next && current) {
          current.replaceWith(next);
        } else if (next && appendParentSelector) {
          document.querySelector(appendParentSelector)?.append(next);
        } else if (!next && current) {
          current.remove();
        }
      }

      updateForm(incoming) {
        const currentForm = document.querySelector("form[data-bundle]");
        const nextForm = incoming.querySelector("form[data-bundle]");
        if (!currentForm || !nextForm) return;

        currentForm.id = nextForm.id;
        currentForm.dataset.parentId = nextForm.dataset.parentId || "";
        currentForm.dataset.price = nextForm.dataset.price || "";
        currentForm.dataset.size = nextForm.dataset.size || "";
        currentForm.dataset.optionalDiscount = nextForm.dataset.optionalDiscount || "";
      }

      updateActive(handle) {
        this.selectors.forEach((selector) => {
          const isCurrent = selector.dataset.handle === handle;
          selector.classList.toggle("active", isCurrent);
          selector.toggleAttribute("aria-current", isCurrent);
        });
      }

      captureSelections() {
        return Array.from(
          document.querySelectorAll("picker-tile[data-tile='bundle-product']")
        ).map((tile) => {
          if (!tile.hasAttribute("aria-checked")) return null;
          const input = tile.querySelector("[data-bundle-input], input[name='id[]']");
          const image = tile.querySelector(".bundle-picker__image");
          return {
            variantId: input?.value || "",
            image: image?.getAttribute("src") || "",
            heading: tile.querySelector(".tile__title-heading")?.textContent || "",
            subheading: tile.querySelector(".tile__title-subheading")?.textContent || "",
          };
        });
      }

      restoreSelections(selections) {
        const tiles = Array.from(
          document.querySelectorAll("picker-tile[data-tile='bundle-product']")
        );
        const drawer = document.querySelector("product-drawer");
        const selectedVariants = {};

        tiles.forEach((tile, index) => {
          const selection = selections[index];
          if (!selection || !selection.variantId) return;

          const image = tile.querySelector(".bundle-picker__image");
          const heading = tile.querySelector(".tile__title-heading");
          const subheading = tile.querySelector(".tile__title-subheading");
          const input = tile.querySelector("[data-bundle-input], input[name='id[]']");

          if (image && selection.image) image.src = selection.image;
          if (heading) heading.textContent = selection.heading;
          if (subheading) subheading.textContent = selection.subheading;
          if (input) input.value = selection.variantId;
          tile.setAttribute("aria-checked", "true");
          selectedVariants[selection.variantId] = tile.dataset.position;
        });

        if (drawer) {
          drawer.selectedVariants = selectedVariants;
          drawer.productTiles = tiles;
          if (typeof drawer.updateDrawerCheckmarks === "function") {
            drawer.updateDrawerCheckmarks();
          }
        }

        const addToCart = document.querySelector("#AddToCart");
        const allFilled =
          tiles.length > 0 && tiles.every((tile) => tile.hasAttribute("aria-checked"));

        if (addToCart) {
          addToCart.disabled = !allFilled;
          addToCart.classList.toggle("button--primary", allFilled);
          addToCart.classList.toggle("button--ternary", !allFilled);
          const text = addToCart.querySelector(".loader-button__text");
          if (text) {
            text.textContent = allFilled
              ? window.translations?.product?.form?.addToCart || "In den Warenkorb"
              : window.translations?.bundles?.chooseAllProducts || text.textContent;
          }
        }

        const lastFilled = [...tiles]
          .reverse()
          .find((tile) => tile.hasAttribute("aria-checked"));
        if (lastFilled && typeof drawer?.updateSelectedInfo === "function") {
          drawer.updateSelectedInfo(
            lastFilled.querySelector(".tile__title-heading")?.textContent || ""
          );
        }
      }
    }
  );
}

(() => {
    "use strict";

    document.addEventListener(
        "click",
        (event) => {
            const sticky = event.target.closest?.(
                "#StickyAddToCart"
            );

            if (!sticky) return;

            const main =
                document.querySelector("#AddToCart");

            if (!main || main.disabled) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            main.click();
        },
        true
    );
})();